const { bootstrap, createBlock, card, data } = window.sitchco.performance;
const { renderCard } = card;
const { formatWeekday, getMonths, groupByDay, monthKey } = data;

function renderDay(day) {
    return `<div class="performance-search-result-card" data-month="${monthKey(day.dateObj)}">
        <div class="performance-search-result-card__date-bubble">
            <span class="performance-search-result-card__day">${formatWeekday(day.dateObj)}</span>
            <span class="performance-search-result-card__day-number">${day.dateObj.getDate()}</span>
        </div>
        <div class="performance-search-result-card__performances">
            ${day.performances.map(renderCard).join('')}
        </div>
    </div>`;
}

function initializeResults(container) {
    const mount = container.querySelector('[data-performances-mount]');
    if (!mount) {
        return;
    }

    const prevBtn = container.querySelector('.performance-search-results__month-nav-btn--prev');
    const nextBtn = container.querySelector('.performance-search-results__month-nav-btn--next');
    const monthTitle = container.querySelector('.performance-search-results__month-title');
    // Derived from the unfiltered set so the nav doesn't shift as filters change.
    let months = [];
    let monthIndex = 0;

    /**
     * The message for "matches exist, just not in the month on screen".
     *
     * Distinct from the filter panel's own, which means "no performance anywhere matches these
     * filters" and is deliberately suppressed while another month still has matches — the case
     * this covers is exactly the one that message would describe falsely. Built here rather than
     * added to the shared Filters class because the copy is specific to a block with a month
     * cursor; the calendar has none, and recomputes its range from the set it was handed.
     */
    const monthEmpty = document.createElement('p');
    monthEmpty.className = 'performance-search-results__month-empty';
    monthEmpty.setAttribute('role', 'status');
    // Dimmed and inerted with the listing it describes. It is a child of the block root rather than
    // of the mount — paint() does mount.innerHTML, which would destroy it every render — so without
    // this it stayed at full opacity through the blocked phase, brighter than the content it
    // annotates.
    monthEmpty.setAttribute('data-performances-dim', '');
    monthEmpty.hidden = true;

    const block = createBlock({
        container,
        name: 'performance-search-results',
        render: paint,
        onData: rememberMonths,
        // Cleared at the start of every load, alongside the shared status line and the filter
        // panel's own message. Left set, a stale "no performances in March" sat beside
        // "Performances are temporarily unavailable" after a failed refresh.
        onLoadStart: () => setMonthEmpty(''),
    });
    if (!block) {
        return;
    }

    // Appended only once the idempotency guard has let us through, so a second init pass can't
    // leave a duplicate <p role="status"> behind.
    container.appendChild(monthEmpty);

    function setMonthEmpty(text) {
        monthEmpty.hidden = !text;
        monthEmpty.textContent = text;
    }

    function updateNav() {
        const month = months[monthIndex];
        if (!(prevBtn && nextBtn && monthTitle)) {
            return;
        }

        monthTitle.textContent = month ? `${month.label} ${month.year}` : ' ';

        prevBtn.disabled = monthIndex === 0;
        prevBtn.classList.toggle('performance-search-results__month-nav-btn--disabled', prevBtn.disabled);

        nextBtn.disabled = monthIndex >= months.length - 1;
        nextBtn.classList.toggle('performance-search-results__month-nav-btn--disabled', nextBtn.disabled);
    }

    function paint(matching) {
        const month = months[monthIndex];
        const inMonth = matching.filter((p) => monthKey(p.dateObj) === month?.key);

        mount.innerHTML = groupByDay(inMonth).map(renderDay).join('');
        updateNav();

        // Decided here, in the one function every render path goes through, rather than in the
        // filter callback: goToMonth() paints without going near the filters, and paging forward
        // into a month the filters emptied is the likelier way to reach this. updateNav() gives no
        // counter-signal either — it derives its months from the unfiltered set, so a zero-match
        // month still shows a normal title with both arrows live.
        const emptyMonth = Boolean(month) && matching.length > 0 && inMonth.length === 0;
        // setMonthEmpty() unhides before it writes: a mutation inside a hidden subtree isn't
        // announced, and un-hiding afterwards doesn't replay it.
        setMonthEmpty(
            emptyMonth
                ? `No performances match the selected filters in ${month.label} ${month.year}. Try another month.`
                : ''
        );
    }

    /**
     * Hold the visible month across a promo refresh by key, not by index — the promo set can start
     * in a different month. Runs before the filter panel is updated, so the nav is derived from the
     * unfiltered set.
     */
    function rememberMonths({ performances }) {
        const activeKey = months[monthIndex]?.key;
        months = getMonths(performances);
        monthIndex = Math.max(
            0,
            months.findIndex((month) => month.key === activeKey)
        );
    }

    function goToMonth(index) {
        // Declined while the block is blocked, like the filter callback: repainting mid-load would
        // narrow the skeletons to a month, and after a failure it would re-slice the previous
        // load's data beside the error. rememberMonths() re-keys monthIndex, so the visitor's month
        // choice survives the next successful load either way.
        if (block.blocked() || index < 0 || index >= months.length || index === monthIndex) {
            return;
        }

        monthIndex = index;
        // Re-slicing the same filtered set, so the runtime is asked for it rather than this block
        // keeping a second copy of both the performances and the predicate.
        paint(block.matching());
    }

    prevBtn?.addEventListener('click', () => goToMonth(monthIndex - 1));
    nextBtn?.addEventListener('click', () => goToMonth(monthIndex + 1));

    block.withFilters(paint);
    block.start();
}

bootstrap('.performance-search-results', 'performance-search-results', initializeResults);
